// TODO: refactor once have better implementation

import { LinkButton } from '@pmi/dsm-react';
import { CircleUserIcon } from '@pmi/dsm-react/dist/components/icons/16';
import styles from './changeLogFormatter.module.scss';
import { LogDataModel } from '../models';

export interface ChangeLogFormatterProps {
  action: string;
  data: LogDataModel | {} | any | null;
  onSeeMore?: (type: string) => void;
}

export default function ChangeLogFormatter({
  action,
  data,
  onSeeMore = (): void => null,
}: ChangeLogFormatterProps) {
  let message: string | any = action;
  const request = data?.request;
  const accountUser = data?.accountUser;
  const result = data?.result;
  const agreementId = data?.agreementProviderId ?? data?.dealId ?? '';
  const hasAccountRole = request?.accountRole;
  // SQL/V3 rows: the platform API pushes the affected person under data.accountAdmin / accountUser(s).
  // Note: nested request objects on those rows are PascalCase (System.Text.Json defaults), hence the
  // `request?.Email ?? request?.email` fallbacks.
  const accountAdmin = data?.accountAdmin;
  const accountUsers: any[] = Array.isArray(data?.accountUsers)
    ? data.accountUsers
    : [];
  const adminLabel =
    [accountAdmin?.firstName, accountAdmin?.lastName, accountAdmin?.email]
      .filter(Boolean)
      .join(' ') || `${accountAdmin?.id ?? data?.id ?? ''}`;
  const adminRole =
    accountAdmin?.role ??
    data?.role ??
    request?.AccountRole ??
    request?.accountRole ??
    '';
  const userLabel = (user: any): string =>
    user?.personName ? `${user.personName} (${user.email})` : user?.email ?? '';
  let hideData = false;

  switch (action) {
    // Deals cases
    case 'Deals.UpdateDealStatus':
      message = `${data?.dealId} status has been updated`;
      break;
    case 'Deals.AddGroup':
      hideData = true;
      message = `${
        request?.name
      } has been added, containing: ${request?.products.join(', ')}`;
      break;
    case 'Deals.UpdatePrimaryContact':
      hideData = true;
      message = `${data?.accountId} Primary Contact updated to ${request?.firstName} ${request?.lastName}`;
      break;
    case 'Deals.RemoveGroup':
      message = `${data?.groupName} has been removed`;
      break;
    case 'Deals.UpdateGroupName':
      message = `Product Group edited to ${request?.name}`;
      break;
    case 'Deals.AddGroupProduct':
      message = `${data?.sku} added to Product Group: ${data?.groupName}`;
      break;
    case 'Deals.RemoveGroupProduct':
      message = `${data?.sku} removed from Product Group: ${data?.groupName}`;
      break;
    case 'Deals.UpdateDealAdminContact':
      hideData = true;
      message = `${data?.accountId} Deal Admin Contact updated to ${request?.firstName} ${request?.lastName}`;
      break;

    case 'DealUsers.AddUsers':
    case 'Users.AddUsers':
      if (action) {
        let type = 'email';
        let count = 0;
        if (request) {
          const contactsCount =
            request.contactsCount ??
            (accountUsers.length ||
              (request.Contacts ?? request.contacts)?.length ||
              0);
          const domains = request.domains ?? request.Domains ?? [];
          if (contactsCount > 0) {
            count = contactsCount;
            if (result) {
              const { contacts } = result;
              if (contacts?.completed?.length > 1) {
                count = contacts?.completed?.length;
              }
            }
          } else {
            count = domains.length;
            type = 'domain';
          }
        }
        message = (
          <span className={styles.linkText}>
            <LinkButton
              titleText={
                type === 'domain'
                  ? `${count} domain${count > 1 ? 's' : ''}`
                  : `${count} user${count > 1 ? 's' : ''}`
              }
              size="sm"
              onClick={() => onSeeMore(type)}
            />
            {type !== 'domain' && (
              <span className={styles.iconContainer}>
                <CircleUserIcon />
              </span>
            )}
            {` ${count > 1 ? 'have' : 'has'} ${
              type === 'domain' ? 'been set up for' : 'been invited to'
            } ${agreementId}`}
          </span>
        );
      }
      break;
    case 'DealUsers.RemoveDealUser':
      hideData = true;
      message = `${request?.email} has been removed as a deal user from: ${data?.dealId}`;
      break;
    case 'DealUsers.AddToDomainBlacklist':
      hideData = true;
      message = `${data?.email} has been added to domainId:${data?.domainId} blacklist`;
      break;
    case 'DealUsers.RemoveFromDomainBlacklist':
      hideData = true;
      message = `${data?.email} has been removed from domainId:${data?.domainId} blacklist`;
      break;
    case 'DealUsers.ResendInvite':
      message = `${data?.accountUserId} invite has been resent`;
      break;
    case 'DealUsers.RemoveAccountUser':
    case 'Users.RemoveUser':
      hideData = true;
      message = `${
        userLabel(accountUser) ||
        `User ${
          data?.removeUserDto?.UserId ?? data?.removeUserDto?.userId ?? ''
        }`
      } has been removed`;
      break;
    case 'Users.DeleteUser':
      hideData = true;
      message = `${
        userLabel(accountUser) || request?.Email || request?.email
      } has been deleted`;
      break;
    case 'Users.UpsertDealUserAccess':
      hideData = true;
      message = `${
        userLabel(accountUser) ||
        data?.upsertUserRequest?.Email ||
        data?.upsertUserRequest?.email
      } access has been updated`;
      break;
    case 'Users.BulkRemoveUser': {
      hideData = true;
      const removedCount =
        accountUsers.length || request?.removeUsers?.length || 0;
      message =
        accountUsers.length > 0 && accountUsers.length <= 3
          ? `${accountUsers.map(userLabel).join(', ')} ${
              accountUsers.length > 1 ? 'have' : 'has'
            } been removed`
          : `${removedCount} user${
              removedCount === 1 ? ' has' : 's have'
            } been removed`;
      break;
    }
    case 'DealUsers.RestoreAccountUser':
      hideData = true;
      if (accountUser?.personName)
        message = `${accountUser?.personName} (${accountUser?.email}) has been restored`;
      else message = `${accountUser?.email} has been restored`;
      break;
    case 'DealUsers.RemoveDomain':
      message = `${request?.domain} has been removed from ${agreementId}`;
      break;
    case 'DealUsers.EditUser':
      hideData = true;
      message = `${accountUser?.email} has been added to ${
        accountUser?.groupId === '*' ? 'All Products' : accountUser?.groupName
      }`;
      break;

    // Admins cases
    case 'Admins.RemoveAdmin':
      message = `Admin has been removed: ${data?.personId} `;
      break;
    case 'Admins.UpdateAdmin':
      hideData = true;
      message = `${request?.email} has been edited to ${
        hasAccountRole ??
        request?.dealRoles
          .map((obj: { roleName: string; dealId: string }) => obj?.roleName)
          .join(', ')
      }`;
      break;

    // Invites cases
    case 'Invites.RemoveInvite':
      message = `${data?.inviteId} has been removed from ${data?.accountId}`;
      break;
    case 'Invites.UpdateInvite':
      message = `Admin had their invitation updated for ${
        hasAccountRole ??
        request?.dealRoles
          .map((obj: { roleName: string; dealId: string }) => obj?.roleName)
          .join(', ')
      }`;
      break;
    case 'Invites.AddInvite':
      hideData = true;
      message = `${request?.email} has been invited to become a ${
        hasAccountRole ??
        request?.dealRoles
          .map((obj: { roleName: string; dealId: string }) => obj?.roleName)
          .join(', ')
      }`;
      break;
    case 'Invites.ResendInvite':
      message = `${data?.inviteId} invite has been resent`;
      break;

    // AccountAdmins cases (SQL/V3 action names)
    case 'AccountAdmins.InviteAdmin':
      hideData = true;
      message = `${
        accountAdmin?.email ?? request?.Email ?? request?.email
      } has been invited to become a ${adminRole}`;
      break;
    case 'AccountAdmins.UpdateAdmin':
      hideData = true;
      message = `${adminLabel} has been edited to ${adminRole}`;
      break;
    case 'AccountAdmins.RemoveAccountAdmin':
      hideData = true;
      message = `${adminLabel} has been removed${
        adminRole ? ` as a ${adminRole}` : ''
      }`;
      break;
    case 'AccountAdmins.UpdateAdminInvite':
      hideData = true;
      message = `${
        accountAdmin?.email ?? 'Admin'
      } had their invitation updated for ${adminRole}`;
      break;
    case 'AccountAdmins.RemoveInvite':
      hideData = true;
      message = `Invitation for ${
        accountAdmin?.email ?? data?.id
      } has been removed${
        data?.accountNumber ? ` from ${data.accountNumber}` : ''
      }`;
      break;
    case 'AccountAdmins.ResendInvite':
      hideData = true;
      message = `${accountAdmin?.email ?? data?.id} invite has been resent`;
      break;

    // Accounts cases
    case 'Accounts.SaveWelcome':
      message = 'Welcome section updated';
      break;
    case 'Accounts.SaveLogo':
      message = 'Company logo updated';
      break;
    case 'Accounts.UpdateDisplayName':
      hideData = true;
      message = `${data?.accountId} updated display name to ${request?.name}`;
      break;
    case 'Accounts.RemoveLogo':
      message = 'Company logo removed';
      break;

    // Power users cases
    case 'PowerUsers.AddPowerUser':
      hideData = true;
      message =
        '[[Email]] has been added as Power User with the role [[RoleType]]';
      break;
    case 'PowerUsers.InvitePowerUser':
      hideData = true;
      message =
        '[[Email]] has been invited as Power User with the role [[RoleType]]';
      break;
    case 'PowerUsers.RemovePowerUser':
      hideData = true;
      message = '[[Id]] has been removed as a Power User';
      break;

    // Profile Controller cases
    case 'ProfileController.AddPhoneNumber':
    case 'Profile.AddPhoneNumber':
      hideData = true;
      message = 'Phone number was added';
      break;
    case 'ProfileController.AddAddress':
    case 'Profile.AddAddress':
      message = 'Address was added';
      break;
    default:
      break;
  }

  return <span data-exclude-lg={hideData ? true : undefined}>{message}</span>;
}
